// Tester unlock session + construction gate (see access-config.js to edit code/list)
import {
  CONSTRUCTION_LOCK,
  TESTER_ACCOUNTS,
  ADMIN_EMAILS,
  TESTER_MAX_USES,
} from "./access-config.js";
import { hasAccessSession } from "./access-code.js";

const SESSION_KEY = "ga_tester_access_v1";
const USAGE_PREFIX = "ga_tester_usage_v1_";
/**
 * Non-persistent unlock. Lives only in this module's memory and resets on
 * every page load (a fresh browser visit always starts locked). A recognised
 * tester must re-enter email + invite code on each visit to enable testing.
 */
let currentSession = null;

/**
 * Public may use personal chart calculator only if lock is off,
 * or a valid tester session exists, or a paid access session exists.
 */
export function canUsePersonalCharts() {
  if (!CONSTRUCTION_LOCK) return true;
  return hasValidTesterSession() || hasAccessSession();
}

export function isConstructionLocked() {
  return CONSTRUCTION_LOCK === true;
}

export function hasValidTesterSession() {
  // In-memory only: true only after a fresh email + code unlock on this load.
  return currentSession !== null;
}

export function getTesterSessionEmail() {
  return currentSession?.email || null;
}

/** True when the current unlocked session belongs to an admin email. */
export function isAdminSession() {
  const email = getTesterSessionEmail();
  return email ? isEmailInList(email, ADMIN_EMAILS) : false;
}

/**
 * Invite code from the unlock session (for server-side /api/interpret auth).
 * Only present after a successful unlock on this browser.
 * @returns {string | null}
 */
export function getTesterSessionCode() {
  const code = currentSession?.code;
  return code ? String(code) : null;
}


/**
 * @param {string} email
 * @param {string} code
 * @returns {{ ok: true, email: string } | { ok: false, error: string }}
 */
export function tryTesterUnlock(email, code) {
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();
  const normalizedCode = String(code || "").trim();

  if (!normalizedEmail || !normalizedEmail.includes("@")) {
    return { ok: false, error: "Enter a valid email address." };
  }
  if (!/^\d+$/.test(normalizedCode)) {
    return { ok: false, error: "Invite code must be numbers only." };
  }
  const account = findTesterAccount(normalizedEmail);
  if (!account) {
    return {
      ok: false,
      error: "That email is not on the tester list. Ask the site owner to add it.",
    };
  }
  // Each tester has their own code — the code must belong to this email.
  if (normalizedCode !== String(account.code).trim()) {
    return { ok: false, error: "Invite code does not match." };
  }

  // In-memory only — no storage write. Resets on the next page load.
  currentSession = {
    email: normalizedEmail,
    code: normalizedCode,
    isAdmin: isEmailInList(normalizedEmail, ADMIN_EMAILS),
  };
  return { ok: true, email: normalizedEmail };
}

export function clearTesterSession() {
  currentSession = null;
  // Defensively clear any legacy persistent session left by older deploys.
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Remaining tester uses before the cap (persisted per email).
 * Admins always return Infinity.
 * @returns {number}
 */
export function getTesterUsesRemaining() {
  if (isAdminSession()) return Infinity;
  const email = getTesterSessionEmail();
  if (!email) return 0;
  return Math.max(0, TESTER_MAX_USES - getUsedCount(email));
}

/** Record one tester use. Admins are not counted. */
export function recordTesterUse() {
  const email = getTesterSessionEmail();
  if (!email || isAdminSession()) return;
  try {
    localStorage.setItem(usageKey(email), String(getUsedCount(email) + 1));
  } catch {
    /* ignore storage failure */
  }
}

/** @param {string} email */
function getUsedCount(email) {
  let raw = 0;
  try {
    raw = parseInt(localStorage.getItem(usageKey(email)) || "0", 10);
  } catch {
    raw = 0;
  }
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

/** @param {string} email */
function usageKey(email) {
  return USAGE_PREFIX + email;
}

/** @param {string} email */
function isEmailInList(email, list) {
  const want = String(email || "").trim().toLowerCase();
  if (!want) return false;
  return list.some(
    (e) =>
      String(e || "").trim().toLowerCase() === want
  );
}

/**
 * Tester account (email + own code) for an email, or null when not listed.
 * @param {string} email
 * @returns {{ email: string, code: string } | null}
 */
function findTesterAccount(email) {
  const want = String(email || "").trim().toLowerCase();
  if (!want) return null;
  return (
    TESTER_ACCOUNTS.find(
      (account) => String(account?.email || "").trim().toLowerCase() === want
    ) || null
  );
}
